import { Option } from '@bbl-digital/snorre-v2'
import type { LabelModel } from 'shared/microServices/portalDocuments/models/LabelModel'

export const findLabelById = (
  nodes: LabelModel[] = [],
  id: string
): LabelModel | null => {
  for (const node of nodes) {
    if (node.id === id) return node
    const found = findLabelById(node.children ?? [], id)
    if (found) return found
  }
  return null
}

export const canMoveUnder = (
  destination: LabelModel,
  movingChildDepth: number,
  maxLevels: number
): boolean => {
  const destLevel = destination.level ?? 0
  const deepestIndexAfterMove = destLevel + 1 + movingChildDepth
  const maxAllowedIndex = maxLevels - 1
  return deepestIndexAfterMove <= maxAllowedIndex
}

export const buildLabelsForDropdownOption = (
  labels: LabelModel[] = []
): Option[] => {
  return labels.map((label) => ({
    label: label.name,
    value: label.id,
    children: buildLabelsForDropdownOption(label.children ?? []),
  }))
}

export const buildDestinationOptions = (
  labels: LabelModel[] = [],
  movingLabel: LabelModel | null,
  maxLevels: number
): Option[] => {
  const movingChildDepth = movingLabel?.childDepth ?? 0

  const mapValid = (nodes: LabelModel[] = []): Option[] => {
    const result: Option[] = []
    for (const node of nodes) {
      if (node.id === movingLabel?.id) continue
      if (!canMoveUnder(node, movingChildDepth, maxLevels)) continue

      const children = mapValid(node.children ?? [])
      result.push({
        label: node.name,
        value: node.id,
        children,
      })
    }
    return result
  }
  return mapValid(labels)
}

export const findOptionByValue = (
  options: Option[] = [],
  value: string
): Option | null => {
  for (const opt of options) {
    if (opt.value === value) return opt
    const child = opt.children ? findOptionByValue(opt.children, value) : null
    if (child) return child
  }
  return null
}

// Ensures each label has a computed level (tree depth), childDepth, and parentId for children
export const annotateLabelsWithHierarchy = (
  nodes: LabelModel[],
  currentLevel = 0,
  currentParentId?: string
): LabelModel[] => {
  return (nodes ?? []).map((node) => {
    const resolvedParentId = node.parentId ?? currentParentId

    const annotatedChildren = annotateLabelsWithHierarchy(
      node.children ?? [],
      currentLevel + 1,
      node.id
    )

    const computedChildDepth = annotatedChildren.length
      ? 1 + Math.max(...annotatedChildren.map((c) => c.childDepth ?? 0))
      : 0

    const annotatedNode: LabelModel = {
      ...node,
      ...(resolvedParentId ? { parentId: resolvedParentId } : {}),
      level: currentLevel,
      childDepth: computedChildDepth,
      children: annotatedChildren,
    }
    return annotatedNode
  })
}
